import resolveWeglotLanguage from '@/services/resolveWeglotLanguage';
import { createSessionLanguageStore, LanguageStore } from '@/services/weglotLanguageStore';

export const WEGLOT_CDN_URL = 'https://cdn.weglot.com/weglot.min.js';

/** The Weglot key for this environment, or empty where localization is not configured.
 *
 * Read per call rather than at import time so a test can vary it, and so nothing
 * captures a value before the runtime config object exists.
 */
function getConfiguredApiKey(): string {
    return deploy.WEB_PUBLIC_WEGLOT_API_KEY || '';
}

/** The element Vue renders into, and so the only part of the page whose text arrives late. */
export const WEGLOT_DYNAMIC_SELECTOR = '#root';

interface WeglotClient {
    initialize(options: { api_key: string, hide_switcher?: boolean, dynamic?: string }): void;
    on(event: 'initialized', handler: () => void): void;
    switchTo(tag: string): void;
    initialized?: boolean;
}

interface BootstrapWeglotOptions {
    apiKey?: string | null;
    loadScript?: (url: string) => Promise<void>;
    store?: LanguageStore;
    getWeglot?: () => WeglotClient | undefined;
}

function loadScriptTag(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Unable to load script: ${url}`));
        document.head.appendChild(script);
    });
}

function getWeglotGlobal(): WeglotClient | undefined {
    return (window as { Weglot?: WeglotClient }).Weglot;
}

/** Fetches Weglot, initializes it, and translates into the given language. */
async function startWeglot(language: string, options: BootstrapWeglotOptions): Promise<boolean> {
    const apiKey = options.apiKey === undefined ? getConfiguredApiKey() : options.apiKey;
    if (!apiKey) {
        console.warn('Weglot is not initialized - no API key is configured.');
        return false;
    }

    try {
        await (options.loadScript || loadScriptTag)(WEGLOT_CDN_URL);
    } catch (error) {
        console.warn('Weglot is not initialized - the script could not be loaded.', error);
        return false;
    }

    const weglot = (options.getWeglot || getWeglotGlobal)();
    if (!weglot) {
        console.warn('Weglot is not initialized - the script exposed no Weglot global.');
        return false;
    }

    try {
        // Registered before initialize(), which is what fires the event.
        weglot.on('initialized', () => weglot.switchTo(language));
        // Weglot translates the DOM as it stands at initialize() and does not watch for
        // later changes on its own. The interview arrives from the API after this runs, so
        // without a container to watch Weglot scans an empty page and translates nothing.
        weglot.initialize({
            api_key: apiKey,
            hide_switcher: true,
            dynamic: WEGLOT_DYNAMIC_SELECTOR,
        });
    } catch (error) {
        console.warn('Weglot is not initialized - initialization failed.', error);
        return false;
    }
    return true;
}

/**
 * Translate the page when the session asks for a language other than English.
 *
 * Localization is a progressive enhancement, so every failure here leaves the app
 * running in English with a warning and nothing else. No path may block or break
 * the interview flow.
 *
 * The script is fetched only when a language actually resolves, so English
 * sessions never pay for Weglot — neither the request nor the CSP exposure.
 *
 * @returns true when Weglot was initialized, false when skipped or unavailable.
 */
export async function bootstrapWeglot(
    location: { search: string, hash: string },
    options: BootstrapWeglotOptions = {},
): Promise<boolean> {
    const store = options.store || createSessionLanguageStore();
    const language = resolveWeglotLanguage(location, store);
    if (!language) {
        return false;
    }

    return startWeglot(language, options);
}

/**
 * Change the language after the page is already running, for the language picker.
 *
 * Weglot is loaded on the first switch away from English rather than up front, so a
 * session that never touches the picker still never fetches it. Once loaded it stays
 * loaded, and later switches are just `switchTo`.
 *
 * @param tag the language to translate into, or null for English.
 * @returns true when the page is now translated, false when it is English.
 */
export async function switchWeglotLanguage(
    tag: string | null,
    options: BootstrapWeglotOptions = {},
): Promise<boolean> {
    const store = options.store || createSessionLanguageStore();
    if (tag) {
        store.write(tag);
    } else {
        store.clear();
    }

    const loaded = (options.getWeglot || getWeglotGlobal)();
    if (loaded?.initialized) {
        // Weglot has no "off" — English is just another language to switch to.
        loaded.switchTo(tag || 'en');
        return Boolean(tag);
    }

    // Nothing to undo: without Weglot loaded the page is already English.
    if (!tag) {
        return false;
    }

    return startWeglot(tag, options);
}
